'use client';

import type { DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useMemo } from 'react';
import { FieldValues, FieldPath, Controller, ControllerProps } from 'react-hook-form';

import { DownArrowIcon } from '../../../icons';

type DatePickerFormProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = Omit<
  DatePickerProps<Dayjs>,
  'value' | 'onChange' | 'renderInput' | 'inputFormat' | 'placeholder'
> &
  Pick<ControllerProps<TFieldValues, TName>, 'name' | 'control'> & {
    placeholderMode?: 'from' | 'to' | 'current' | 'default';
    placeholder?: string;
    format?: string;
  };

export const DatePickerForm = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: DatePickerFormProps<TFieldValues, TName>
) => {
  const { name, control, placeholderMode, placeholder, format = 'DD.MM.YYYY', ...datePickerProps } = props;

  const placeholderString = useMemo(() => {
    if (placeholder) {
      return placeholder;
    }

    switch (placeholderMode) {
      case 'from':
        return dayjs().startOf('month').format('DD.MM.YYYY');
      case 'current':
      case 'to':
        return dayjs().format('DD.MM.YYYY');
      default:
        return '__.__.____';
    }
  }, [placeholder, placeholderMode]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, ...fields }, fieldState: { error } }) => (
        <DatePicker
          {...datePickerProps}
          {...fields}
          value={value ? (dayjs.isDayjs(value) ? value : dayjs(value)) : null}
          format={format}
          slots={{
            openPickerIcon: (props) => <DownArrowIcon {...props} color="#fff" />,
          }}
          slotProps={{
            textField: {
              placeholder: placeholderString,
              error: error != null,
              helperText: error?.message,
            },
          }}
        />
      )}
    />
  );
};
